import { useState, useEffect } from 'react';
import { configApi } from '../services/api';

export interface ConfigSettings {
  developmentEmail: string;
  slackWebhookUrl: string;
  daysBeforeEscalation: string;
  sesConfigurationSet: string;
  sourceEmail: string;
  isDevelopmentMode: boolean;
  developmentSlackAlias?: string;
  useHtmlEmails?: boolean;
  domainOwnerEmail?: string;
  domainOwnerFirstName?: string;
  domainOwnerLastName?: string;
  domainOwnerTitle?: string;
  domainOwnerPhone?: string;
  domainOwnerAlias?: string;
}

export function useConfig() {
  const [config, setConfig] = useState<ConfigSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [testResults, setTestResults] = useState<any>(null);
  const [testLoading, setTestLoading] = useState<boolean>(false);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await configApi.getAll();
      console.log('API Response:', response.data); // Debug log
      // Extract config from API response
      if (response.data) {
        setConfig(response.data as ConfigSettings);
      } else {
        // If response doesn't match expected format, use safely typed fallback
        console.warn('Unexpected API response format', response.data);
        setConfig({
          developmentEmail: 'test@example.com',
          slackWebhookUrl: '',
          daysBeforeEscalation: '3',
          sesConfigurationSet: 'EmailTrackingConfigSet',
          sourceEmail: 'noreply@example.com',
          isDevelopmentMode: true,
          useHtmlEmails: false, // Default to plain text
          domainOwnerEmail: 'aws-solutions@amazon.com',
          domainOwnerFirstName: 'AWS',
          domainOwnerLastName: 'Solutions',
          domainOwnerTitle: 'Solutions Architect',
          domainOwnerPhone: '1-800-AWS-HELP',
          domainOwnerAlias: 'aws-solutions'
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
      console.error('Failed to fetch configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (newConfig: ConfigSettings): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await configApi.update(newConfig);
      if (response.data && response.data.data) {
        setConfig(response.data.data as ConfigSettings);
      }
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update configuration'));
      console.error('Failed to update configuration:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const testConnections = async (): Promise<any> => {
    setTestLoading(true);
    setTestResults(null);
    try {
      const response = await configApi.testConnection();
      if (response.data && response.data.data) {
        const results = response.data.data.results;
        setTestResults({
          success: true,
          ...results
        });
        return results;
      }
      throw new Error('Invalid response from test connection endpoint');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setTestResults({
        success: false,
        error: errorMessage
      });
      console.error('Connection test failed:', err);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setTestLoading(false);
    }
  };

  // Fetch configuration on component mount
  useEffect(() => {
    fetchConfig();
  }, []);

  return {
    config,
    loading,
    error,
    testResults,
    testLoading,
    fetchConfig,
    updateConfig,
    testConnections
  };
}

export default useConfig;
